import { ReactNode } from 'react';
import { CloseIcon } from '../../bombiix-icon';
import { TruncatedText } from '../TruncatedText';

export type BombiixChipVariant = 'purple' | 'blue' | 'green' | 'red' | 'amber' | 'khaki' | 'grey' | 'pink' | 'orange';

/**
 * Deliberately below the 24px house default: the master-design chip is 18px
 * tall (Figma node 12714:186802), so a 24px glyph cannot sit inside it.
 */
const REMOVE_ICON_SIZE = 16;

interface BombiixChipBaseProps {
  /** A string is clamped to one line with a hover tooltip; a node is rendered as-is. */
  children: ReactNode;
  /** Fill colour, mapped to the `.bombiix-chip--*` variants in `_chip.scss`. */
  variant?: BombiixChipVariant;
  className?: string;
}

/** `onRemove` and `removeLabel` travel together — a remove control always carries an accessible name. */
type BombiixChipRemoveProps = { onRemove: () => void; removeLabel: string } | { onRemove?: never; removeLabel?: never };

export type BombiixChipProps = BombiixChipBaseProps & BombiixChipRemoveProps;

/**
 * BombiixChip — master-design chip pill (`.bombiix-chip`). Static by default;
 * pass `onRemove` + `removeLabel` to render the inline close control used
 * where picks sit below a select rather than inside its control.
 *
 * The pill is `inline-flex`, so its own `text-overflow: ellipsis` never reaches
 * the text (an anonymous flex item) — string children go through TruncatedText
 * instead, which clamps and adds the overflow-only tooltip.
 */
function BombiixChip({ children, variant = 'purple', className, onRemove, removeLabel }: BombiixChipProps) {
  const rootClass = ['bombiix-chip', `bombiix-chip--${variant}`, onRemove ? 'bombiix-chip--removable' : '', className].filter(Boolean).join(' ');
  return (
    <span className={rootClass}>
      {typeof children === 'string' ? <TruncatedText text={children} /> : children}
      {onRemove ? (
        <button type="button" className="bombiix-chip__remove" aria-label={removeLabel} onClick={onRemove}>
          <CloseIcon size={REMOVE_ICON_SIZE} />
        </button>
      ) : null}
    </span>
  );
}

export default BombiixChip;
